import { useMemo, useState } from 'react';
import { SubmitHandler } from 'react-hook-form';
import { useMutation } from '@tanstack/react-query';
import { Box } from '@givebutter/primitives/box';
import { Button } from '@givebutter/primitives/button';
import { Separator } from '@givebutter/primitives/separator';
import { useToast } from '@givebutter/primitives/toast';
import { createSubscriptionCallback, createSubscriptionStripeSession } from 'api/subscriptions';
import { ChatWithSales } from 'assets/js/pages/account-settings/account-about/givebutter-plus/ChatWithSales';
import * as GivebutterPlusTrialDialog from 'assets/js/pages/account-settings/account-about/givebutter-plus/GivebutterPlusTrialDialog';
import { OverageMessage } from 'assets/js/pages/account-settings/account-about/givebutter-plus/OverageMessage';
import { SubscriptionFaqs } from 'assets/js/pages/account-settings/account-about/givebutter-plus/SubscriptionFaqs';
import * as SubscriptionIntervalRadioGroup from 'assets/js/pages/account-settings/account-about/givebutter-plus/SubscriptionIntervalRadioGroup';
import { SubscriptionPlanLineItems } from 'assets/js/pages/account-settings/account-about/givebutter-plus/SubscriptionPlanLineItems';
import { SubscriptionPlusCallout } from 'assets/js/pages/account-settings/account-about/givebutter-plus/SubscriptionPlusCallout';
import { SubscriptionSettingsFormValues } from 'assets/js/pages/account-settings/account-about/givebutter-plus/types';
import { useDaysRemainingOnTrial } from 'assets/js/pages/account-settings/account-about/givebutter-plus/useDaysRemainingOnTrial';
import { useSubscriptionFormTable } from 'assets/js/pages/account-settings/account-about/givebutter-plus/useSubscriptionFormTable';
import { useSubscriptionPlans } from 'assets/js/pages/account-settings/account-about/givebutter-plus/useSubscriptionPlans';
import * as Form from 'components/form';
import * as StripeCheckoutDialog from 'components/stripe-checkout-dialog';
import * as Table from 'components/table';
import { GIVEBUTTER_PRIVACY_POLICY_LINK, GIVEBUTTER_TOS_LINK } from 'constants/links';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useQueryParams } from 'customHooks/useQueryParams';
import GivebutterPlusLogo from 'images/givebutter-plus-logo.svg';
import { accountQueryKeys } from 'queries/account-query-keys';
import { queryClient } from 'queries/queryClient';
import { twMerge } from 'tailwind-merge';
import { SubscriptionTier } from 'types/domain';
import { GivebutterAxiosError } from 'types/error';

interface SubscriptionFormNewProps {
    onSuccess?: () => void;
}
const SubscriptionFormNew = ({ onSuccess }: SubscriptionFormNewProps) => {
    const { accountId, isCurrentlyOnFreeTrial, isFreeTrialExpired, validGivebutterPlusPlan } =
        useCurrentAccount();
    const message = useToast();
    const { data: plans } = useSubscriptionPlans();

    const [clientSecret, setClientSecret] = useState('');
    const [sessionId, setSessionId] = useState('');
    const [isStripeDialogOpen, setIsStripeDialogOpen] = useState(false);
    const [isPaymentCompleted, setIsPaymentCompleted] = useState(false);
    const queryParams = useQueryParams<{ plus_trial: string }>();
    const hasPlusTrialQueryParam = queryParams.plus_trial === 'true';
    const [isFreeTrialDialogOpen, setIsFreeTrialDialogOpen] = useState(
        hasPlusTrialQueryParam && !isCurrentlyOnFreeTrial && !validGivebutterPlusPlan,
    );
    const recommendedPlan = plans.find(plan => plan.is_recommend);
    const defaultPlanId = recommendedPlan?.id ?? plans[0].id;
    const shouldDisablePeriodSelection = !!recommendedPlan?.recommended_plan_period_type;

    const methods = Form.useGivebutterForm<SubscriptionSettingsFormValues>({
        defaultValues: {
            tier: defaultPlanId as SubscriptionTier,
            billingPeriod: recommendedPlan?.recommended_plan_period_type ?? 'year',
        },
    });
    const { control, handleSubmit, path, watch, resetFormButton } = methods;
    const [billingPeriod] = watch(['billingPeriod']);
    const showAnnualPricing = billingPeriod === 'year';

    const { table } = useSubscriptionFormTable({ defaultPlanId, showAnnualPricing });
    const rowSelection = table.getState().rowSelection;

    const selectedPlan = useMemo(
        () => plans.find(plan => rowSelection[plan.id]),
        [plans, rowSelection],
    );

    const showChatWithSalesMsg = useMemo(() => {
        if (!selectedPlan) {
            return false;
        }
        const selectedIndex = plans.findIndex(plan => plan.id === selectedPlan.id);
        return selectedIndex >= plans.length - 2;
    }, [selectedPlan, plans]);

    const { mutate: submit, isPending: isPendingStripeSession } = useMutation({
        mutationFn: (_: SubscriptionSettingsFormValues) =>
            createSubscriptionStripeSession(accountId, {
                product: 'plus',
                tier: selectedPlan?.id as SubscriptionTier,
                billing_period: billingPeriod,
            }),
        onSuccess: response => {
            setSessionId(response.session_id);
            setClientSecret(response.client_secret);
            setIsStripeDialogOpen(true);
        },
        onError: (error: GivebutterAxiosError) => {
            message.error(error.response?.data.message ?? 'Unknown error occurred');
        },
        onSettled: () => {
            resetFormButton();
        },
    });

    const { mutate: submitCallback } = useMutation({
        mutationFn: () =>
            createSubscriptionCallback(accountId, {
                session_id: sessionId,
            }),
        onSuccess: () => {
            setIsPaymentCompleted(true);
        },
        onError: (error: GivebutterAxiosError) => {
            message.error(error.response?.data.message ?? 'Unknown error occurred');
        },
    });

    const daysRemainingOnTrial = useDaysRemainingOnTrial();

    return (
        <div className="flex flex-col gap-6">
            <div className="flex flex-col gap-3">
                <img className={'max-w-48'} src={GivebutterPlusLogo} alt="Givebutter Plus logo" />
                <p className="text-xl font-medium text-gray-700">
                    Upgrade and unlock the full potential of your favorite fundraising tool.
                </p>
            </div>
            <Form.Provider {...methods}>
                <form
                    onSubmit={handleSubmit(submit as SubmitHandler<SubscriptionSettingsFormValues>)}
                    className="flex flex-wrap gap-8 xl:flex-nowrap"
                >
                    <div className="flex w-full flex-col gap-3">
                        <SubscriptionIntervalRadioGroup.Root
                            control={control}
                            name={path('billingPeriod')}
                            disabled={shouldDisablePeriodSelection}
                        >
                            <SubscriptionIntervalRadioGroup.Monthly />
                            <SubscriptionIntervalRadioGroup.Annually />
                        </SubscriptionIntervalRadioGroup.Root>
                        <Table.Root variant={'bordered'} table={table}>
                            <div className={'flex flex-col gap-4'}>
                                <Box className={'overflow-x-auto'}>
                                    <Table.Content>
                                        <Table.Header className="text-xs text-gray-600" />
                                        <Table.Body />
                                    </Table.Content>
                                </Box>
                            </div>
                        </Table.Root>
                        <OverageMessage billingPeriod={billingPeriod} />
                    </div>
                    {selectedPlan && (
                        <div className="flex grow flex-col gap-4">
                            <SubscriptionPlanLineItems
                                plan={selectedPlan}
                                showAnnualPricing={showAnnualPricing}
                            />

                            <div className="flex flex-col gap-2">
                                <Form.SubmitButton isSubmitting={isPendingStripeSession}>
                                    Upgrade to Givebutter Plus
                                </Form.SubmitButton>

                                {!isFreeTrialExpired && (
                                    <GivebutterPlusTrialDialog.Root
                                        onSuccess={() => {
                                            setIsFreeTrialDialogOpen(false);
                                            onSuccess?.();
                                        }}
                                        open={isFreeTrialDialogOpen}
                                        onOpenChange={open => setIsFreeTrialDialogOpen(open)}
                                    >
                                        <GivebutterPlusTrialDialog.Trigger asChild>
                                            <Button
                                                variant="outline"
                                                color="neutral"
                                                disabled={isCurrentlyOnFreeTrial}
                                            >
                                                {isCurrentlyOnFreeTrial
                                                    ? `${daysRemainingOnTrial} left of trial`
                                                    : 'Start 30-day free trial'}
                                            </Button>
                                        </GivebutterPlusTrialDialog.Trigger>
                                    </GivebutterPlusTrialDialog.Root>
                                )}

                                <p className="text-xs text-gray-600">
                                    By subscribing to Givebutter Plus you agree to our{' '}
                                    <a
                                        href={GIVEBUTTER_TOS_LINK}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="text-gray-600 underline"
                                    >
                                        Terms of Use
                                    </a>{' '}
                                    and{' '}
                                    <a
                                        href={GIVEBUTTER_PRIVACY_POLICY_LINK}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="text-gray-600 underline"
                                    >
                                        Privacy Policy
                                    </a>
                                    .
                                </p>
                            </div>

                            <ChatWithSales
                                className={twMerge('invisible', showChatWithSalesMsg && 'visible')}
                            />
                        </div>
                    )}
                </form>
            </Form.Provider>
            <SubscriptionPlusCallout />
            <Separator />
            <SubscriptionFaqs />
            <StripeCheckoutDialog.Root
                clientSecret={clientSecret}
                open={isStripeDialogOpen}
                onOpenChange={open => {
                    setIsStripeDialogOpen(open);
                    if (!open && isPaymentCompleted) {
                        onSuccess?.();
                        queryClient.invalidateQueries({
                            queryKey: accountQueryKeys.current(accountId),
                        });
                    }
                }}
                onComplete={() => submitCallback()}
                showOverlay={true}
            />
        </div>
    );
};

export { SubscriptionFormNew };
